/**
 * Application principale — Navigation réorganisée
 * @see NAV-REORG-001 (projet/architecture/NAV-REORGANISATION-IMPLEMENTATION.md)
 *
 * Patterns :
 *   - /login         : public, aucun layout
 *   - /vocal         : VocalLayout (minimaliste, bouton retour uniquement)
 *   - /vocal2        : démo DPI simulé (page /dpi plein écran + widget flottant, CONC3)
 *   - Tout le reste  : AppShell (header + sidebar + Outlet)
 *
 * RBAC :
 *   - Parent AppShell : guard "authentifié" uniquement
 *   - Chaque route sensible : son propre ProtectedRouteWithRoles
 */

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/contexts/AuthContext';

// ── Layouts ──────────────────────────────────────────────────────────────────
import { AppShell }    from '@/layouts/AppShell';
import { VocalLayout } from '@/layouts/VocalLayout';
import { PersonaProvider } from '@/shared/contexts/PersonaContext';

// ── Guards ───────────────────────────────────────────────────────────────────
import { ProtectedRoute }          from '@/shared/components/ProtectedRoute';
import { ProtectedRouteWithRoles } from '@/shared/components/ProtectedRouteWithRoles';

// ── Pages — AUTH ─────────────────────────────────────────────────────────────
import { LoginPage } from '@/features/auth/pages/LoginPage';

// ── Pages — VOCAL ────────────────────────────────────────────────────────────
import { HoriPage } from '@/features/soignant/pages/HoriPage';
import { VocalTutoPage } from '@/features/soignant/pages/VocalTutoPage';
import { VocalScenariosPage } from '@/features/soignant/pages/VocalScenariosPage';
import { VocalDpiPage } from '@/features/soignant/pages/VocalDpiPage';

// ── Pages — ACCUEIL ──────────────────────────────────────────────────────────
import { HomePage } from '@/pages/HomePage';

// ── Pages — CLINIQUE ─────────────────────────────────────────────────────────
import { PatientsPage }        from '@/features/admin/pages/PatientsPage';
import { PatientTimelinePage } from '@/features/admin/pages/PatientTimelinePage';

// ── Pages — ADMINISTRATION ───────────────────────────────────────────────────
import { UsersPage }    from '@/features/admin/pages/UsersPage';
import { LLMConfigPage } from '@/features/admin/pages/LLMConfigPage';

// ── Pages — PRIORITAIRES admin ────────────────────────────────────────────────
import { DesignSystemPage } from '@/features/admin/pages/DesignSystemPage';
import { AgentsPage }       from '@/features/admin/pages/AgentsPage';

// ── Pages — SUPERVISION ──────────────────────────────────────────────────────
import { PolicyPage }     from '@/features/admin/pages/PolicyPage';
import { TracesPage }     from '@/features/admin/pages/TracesPage';
import { SupervisorPage } from '@/features/admin/pages/SupervisorPage';
import { VisionPage }     from '@/features/admin/pages/VisionPage';

// ── Pages — INTÉGRATIONS ─────────────────────────────────────────────────────
import { PmsiDemoPage }      from '@/features/admin/pages/PmsiDemoPage';
import { DpiLivePage }       from '@/features/admin/pages/DpiLivePage';

// ── ProfilEnum pour les guards ────────────────────────────────────────────────
import { ProfilEnum } from '@/shared/types/user';

const ADMIN_ONLY  = [ProfilEnum.ADMIN];
const ADMIN_CADRE = [ProfilEnum.ADMIN, ProfilEnum.CADRE];

// ── Plugins à dépôt propre (ADR-ARCH-MODULAIRE-001) : écrans par chemin, montés en /app/plugins ──
import type { ReactElement } from 'react';
import { PLUGIN_PAGES } from '@/shared/plugins';

const PLUGIN_ELEMENTS: Record<string, ReactElement> = Object.assign(
  {},
  ...Object.values(
    import.meta.glob<{ elements: Record<string, ReactElement> }>('../plugins/*/horaliia-plugin/ui/routes.tsx', { eager: true }),
  ).map((m) => m.elements),
);

// ─────────────────────────────────────────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>

          {/* ── PUBLIC ─────────────────────────────────────────────────────── */}
          <Route path="/login" element={<LoginPage />} />

          {/* ── VOCAL — Layout minimaliste + sélecteur d'onglets (sous-routes) ─ */}
          <Route
            path="/vocal"
            element={
              <ProtectedRoute>
                <VocalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<HoriPage />} />
            <Route path="tuto" element={<VocalTutoPage />} />
            <Route path="scenarios" element={<VocalScenariosPage />} />
          </Route>

          {/* ── VOCAL2 — démo DPI simulé : page /dpi plein écran, sans barre d'onglets (CONC3) ─ */}
          <Route
            path="/vocal2"
            element={
              <ProtectedRoute>
                <PersonaProvider>
                  <VocalDpiPage />
                </PersonaProvider>
              </ProtectedRoute>
            }
          />

          {/* ── TOUTES LES AUTRES PAGES — AppShell (header + sidebar) ──────── */}
          {/*    Guard parent : authentifié uniquement                          */}
          {/*    Guards enfants : par profil si nécessaire                      */}
          <Route
            element={
              <ProtectedRoute>
                <AppShell />
              </ProtectedRoute>
            }
          >

            {/* ACCUEIL */}
            <Route path="/home" element={<HomePage />} />

            {/* CLINIQUE — tous profils */}
            <Route path="/dpi"                             element={<DpiLivePage />} />
            <Route path="/patients"                        element={<PatientsPage />} />
            <Route path="/patients/:patientId/timeline"    element={<PatientTimelinePage />} />

            {/* ADMINISTRATION — admin uniquement */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_ONLY}>
                  <UsersPage />
                </ProtectedRouteWithRoles>
              }
            />
            <Route
              path="/admin/patients"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_ONLY}>
                  <PatientsPage />
                </ProtectedRouteWithRoles>
              }
            />
            <Route
              path="/admin/llm-config"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_ONLY}>
                  <LLMConfigPage />
                </ProtectedRouteWithRoles>
              }
            />

            {/* PRIORITAIRES — admin uniquement */}
            <Route
              path="/admin/design-system"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_ONLY}>
                  <DesignSystemPage />
                </ProtectedRouteWithRoles>
              }
            />
            <Route
              path="/admin/agents"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_ONLY}>
                  <AgentsPage />
                </ProtectedRouteWithRoles>
              }
            />

            {/* SUPERVISION — admin + cadre */}
            <Route
              path="/admin/policy"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_CADRE}>
                  <PolicyPage />
                </ProtectedRouteWithRoles>
              }
            />
            <Route
              path="/admin/traces"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_CADRE}>
                  <TracesPage />
                </ProtectedRouteWithRoles>
              }
            />
            <Route
              path="/admin/supervisor"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_CADRE}>
                  <SupervisorPage />
                </ProtectedRouteWithRoles>
              }
            />
            <Route
              path="/admin/vision"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_CADRE}>
                  <VisionPage />
                </ProtectedRouteWithRoles>
              }
            />

            {/* INTÉGRATIONS — admin uniquement */}
            <Route
              path="/admin/demo-pmsi"
              element={
                <ProtectedRouteWithRoles allowedProfils={ADMIN_ONLY}>
                  <PmsiDemoPage />
                </ProtectedRouteWithRoles>
              }
            />

            {/* PLUGINS à dépôt propre — toute page de la navigation est routée ; sans écran, l'erreur se voit */}
            {PLUGIN_PAGES.map((p) => (
              <Route
                key={p.path}
                path={p.path}
                element={
                  <ProtectedRouteWithRoles allowedProfils={p.profils}>
                    {PLUGIN_ELEMENTS[p.path] ?? <p role="alert">Plugin incomplet : aucun écran pour {p.path}</p>}
                  </ProtectedRouteWithRoles>
                }
              />
            ))}

          </Route>

          {/* ── REDIRECTS — aucune route n'est perdue ──────────────────────── */}
          <Route path="/dashboard"     element={<Navigate to="/home"            replace />} />
          <Route path="/admin"         element={<Navigate to="/admin/users"     replace />} />
          <Route path="/demo/pmsi"     element={<Navigate to="/admin/demo-pmsi" replace />} />
          <Route path="/"              element={<Navigate to="/home"           replace />} />
          <Route path="*"              element={<Navigate to="/home"           replace />} />

        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
